import { DeviceStatus } from '@/api/deviceApi'
import type { TableRowVariant } from '@/components/common-table/types'

/**
 * What the device list shows. `normal` is the unarchived default, since
 * active already means connected to users.
 *
 * Not to be confused with `config/deviceStatus` or `types/deviceStatus`, which own the derived
 * `status` column a project can define for its devices.
 */
export type DeviceView = 'normal' | 'archived'

export const DEFAULT_DEVICE_VIEW: DeviceView = 'normal'

export const isDeviceView = (value: unknown): value is DeviceView => value === 'normal' || value === 'archived'

/** A status to request, plus whether an `all` request should keep archived devices in. */
export interface DeviceListRequest {
	status: DeviceStatus
	includeArchived: boolean
}

/**
 * The request a view resolves to. Archived is `active = false` server-side until the backend grows a
 * visibility flag, so the three views map onto the status param plus the archived opt-in. This is the
 * one place that mapping lives.
 */
export function deviceViewRequest(view: DeviceView): DeviceListRequest {
	switch (view) {
		case 'archived':
			return { status: DeviceStatus.Inactive, includeArchived: false }
		default:
			return { status: DeviceStatus.Active, includeArchived: false }
	}
}

/**
 * The view resolves the `all` tab into what the list actually shows. A caller that named `active` or
 * `inactive` outright keeps it, so a Device Management preference cannot reach the dialogs that share
 * its store.
 */
export function resolveDeviceListRequest(status: DeviceStatus, view: DeviceView): DeviceListRequest {
	return status === DeviceStatus.All ? deviceViewRequest(view) : { status, includeArchived: false }
}

export interface DeviceRowVariantOptions {
	device: { id: string | number; status?: string }
	deviceView: DeviceView
	archivedDeviceIds?: ReadonlySet<string>
}

/**
 * Determines whether a device should be treated as archived or hidden.
 */
export function isDeviceArchived({ device, deviceView, archivedDeviceIds }: DeviceRowVariantOptions): boolean {
	if (deviceView === 'archived') {
		return true
	}
	if (deviceView === 'normal') {
		return false
	}
	if (archivedDeviceIds?.has(String(device.id))) {
		return true
	}
	return device.status === DeviceStatus.Inactive
}

/**
 * Resolves the table row variant for a device.
 * Extensible for future warning, danger, or custom device row states.
 */
export function getDeviceRowVariant(options: DeviceRowVariantOptions): TableRowVariant | undefined {
	if (isDeviceArchived(options)) {
		return 'archived'
	}
	return undefined
}
